import {
  ActionButton,
  FontIcon,
  IIconProps,
  mergeStyles,
  Spinner,
  SpinnerSize,
  Stack,
  TooltipHost,
} from "@fluentui/react";
import React from "react";
import { connect, ConnectedProps } from "react-redux";
import authModule from "../../../../utilities/AuthModule";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { ComponentTypes } from "../../config/config";
import { DocubirdCache, extensions, validSigningRequestExtensions } from "../../config/constants";
import { confirmDeleteButtonStyles } from "../../config/controlStyles";
import { IDriveInfo } from "../../interfaces/IDriveInfo";
import { IFile } from "../../interfaces/IFile";
import { IFileItem } from "../../interfaces/IFileItem";
import { IPageSettings } from "../../interfaces/IPageSettings";
import { RootState } from "../../redux";
import { setContentManagerMoveTo } from "../../redux/modules/contentManagerDirectory";
import { getFileItemInfo, getItem, getItemWebViewUrl } from "../../services/GraphService";
import { isMobile, mobileListItemStyle } from "../../styles/mobileStyles";
import { ContentManagerCopyMoveModeEnum } from "../enums/ContentManagerCopyMoveModeEnum";
import { ModeEnum } from "../enums/modeEnum";
import { EmailCreatorFactory } from "../helpers/emailAttachment/EmailCreatorFactory";
import { FileDownLoader } from "../helpers/fileDownloader";
import { Fileview } from "../_v2/components/Explorer/Fileview/Fileview";
import { AutomationPanel } from "../_v2/components/Panels/AutomationPanel/AutomationPanel";
import { useCheckPiiMutation, useGraphItemPiiMutation } from "../_v2/redux/api/local/api-pii-slice";
import { useLazyGetItemByUniqueIDQuery, useLazySendSharepointItemToRecyclebinQuery } from "../_v2/redux/api/sharepoint/api-sharepoint-slice";
import { WebPanel } from "../_v2/_shared/WebPanel/WebPanel";
import { SigningPanel } from "../_v2/components/Panels/SigningPanel/SigningPanel";
import { CacheHelper } from "../../../../utilities/CacheHelper";
import { getValidHooks } from "../../services/HooksService";
import { HookType } from "../_v2/redux/api/local/api-local-interfaces";
import { getFileNameDownloadTemplateAvailable, getFilenameFromTemplate } from "../../../../utilities/FileNameDownloadTemplateHelper";
import { CMFileSharePanel, SharePanelMode } from "../../../../contentManager/Panels/CMFileSharePanel";

const minItems = 5;

const mapStateToProps = (state: RootState) => {
  return {
    mode: state.mode.mode,
    user: state.user,
    siteInfo: state.tenantInfo?.sites,
    oneDriveRootDetails: state.workingDirectories.oneDriveRootDetails,
    currentSharepointFolder: state.currentSharepointFolder,
    searchValue: state.search.searchValue.value,
    enableSharing: state.tenantInfo?.enableSharing,
  };
};

const mapDispatchToProps = {
  setContentManagerMoveTo
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface IMultipleFilesSeletedProps extends PropsFromRedux {
  files: IFile[];
  isGraphQuery: boolean;
  currentDirectory: IPageSettings;
  workingDirectories: IPageSettings[];
  updateMultipleFilesPii?: (files: IFile[]) => void;
  filesDeleted?: () => void;
  openConfirmSave?: () => void;
  setIsMultipleFilesSelectedPanelOpen: (isOpen: boolean) => void;
  openPropertiesTrigger?: () => void;
}

const privacySensitiveIcon = { iconName: "Unlock" };
const redactIcon = { iconName: "PageEdit" };
const fileDeleteIcon = { iconName: "Delete" };
const fileDownloadIcon = { iconName: "Download" };
const propertiesIcon = { iconName: "Info" }
const linkIcon: IIconProps = { iconName: "AddLink" };
const attachIcon: IIconProps = { iconName: "Attach" };
const saveIcon: IIconProps = { iconName: "Save" };
const copyLinkIcon = { iconName: "Link" };

const MultipleFilesSeleted = (props: IMultipleFilesSeletedProps) => {
  const { files, currentDirectory, workingDirectories, mode, user, isGraphQuery, updateMultipleFilesPii, filesDeleted, openConfirmSave, setContentManagerMoveTo, setIsMultipleFilesSelectedPanelOpen } = props;
  const languageStrings = React.useContext(LanguageContext);
  const [emailCreatorfactory] = React.useState<EmailCreatorFactory>(
    new EmailCreatorFactory(currentDirectory?.isTeamsFolder)
  );

  const [selectedFiles, setSelectedFiles] = React.useState(files);
  const [fileViews, setFileViews] = React.useState<JSX.Element[]>([]);
  const [linkCount, setLinkCount] = React.useState(0);
  const [loadingLinks, setLoadingLinks] = React.useState(false);
  const [loadingAttachments, setLoadingAttachments] = React.useState(false);

  const [showRedact, setShowRedact] = React.useState<boolean>(false);
  const [isCheckingPii, setIsCheckingPii] = React.useState<boolean>(false);
  const [isRedactingData, setIsRedactingData] = React.useState<boolean>(false);
  const [isDeletingFiles, setIsDeletingFiles] = React.useState<boolean>(false);
  const [isDownloadingFiles, setIsDownloadingFiles] = React.useState<boolean>(false);
  const [confirmDelete, setConfirmDelete] = React.useState<boolean>(false);

  const [approvalFlowsAvailable, setApprovalFlowsAvailable] = React.useState(false);
  const [signingFlowsAvailable, setSigningFlowsAvailable] = React.useState(false);

  const [checkPiiMutation] = useCheckPiiMutation();
  const [checkGraphPiiMutation] = useGraphItemPiiMutation();
  const [sendSharepointItemToRecyclebin] = useLazySendSharepointItemToRecyclebinQuery();
  const [getItemByUniqueId] = useLazyGetItemByUniqueIDQuery();

  React.useEffect(() => {
    setFileViews(
      selectedFiles.map((item, index) => (
        <div key={index}>
          <Fileview
            item={item}
            isMobile={isMobile()}
            multiSelect={false}
            hasShowMore={false}
            tenantEnablePII={user.tenantEnablePII}
          />
        </div>

      ))
    );
  }, [selectedFiles]);

  React.useEffect(() => {
    let showRedactButton = false;
    if (isRedactingData) {
      showRedactButton = true;
    }
    else {
      selectedFiles.forEach(selectedFile => {
        if (user.tenantEnableRedaction) {
          if (selectedFile.fileType === extensions.pdf && selectedFile.pii?.piiDetected && !showRedactButton) {
            showRedactButton = true;
          }
        }
      });
    }

    setShowRedact(showRedactButton);
  }, [selectedFiles, isRedactingData]);

  React.useEffect(() => {
    const validHooks = user?.tenantEnableApprovalHooks || CacheHelper.get(DocubirdCache.signingEnabled) === true
      ? getValidHooks(CacheHelper.get(DocubirdCache.hooks), currentDirectory)
      : [];

    setApprovalFlowsAvailable(user?.tenantEnableApprovalHooks && validHooks.filter(h => h.typeId === HookType.Approval || h.typeId === HookType.BatchApproval || h.typeId === HookType.Generic).length > 0);
    setSigningFlowsAvailable(CacheHelper.get(DocubirdCache.signingEnabled) === true && validHooks.filter(h => h.typeId === HookType.Sign).length > 0);
  }, [currentDirectory, user]);

  const checkPii = async (redactPiiData: boolean) => {
    setIsCheckingPii(!redactPiiData);
    setIsRedactingData(redactPiiData);

    const tempFiles: IFile[] = [];
    for (let index = 0; index < selectedFiles.length; index++) {
      if (selectedFiles[index].pii && !redactPiiData) {
        tempFiles.push({
          ...selectedFiles[index],
          pii: {
            piiDetected: false,
            pii: null,
            isChecking: true
          }
        });
      }
      else if (selectedFiles[index].pii?.piiDetected && redactPiiData && selectedFiles[index].fileType === extensions.pdf) {
        tempFiles.push({
          ...selectedFiles[index],
          pii: {
            piiDetected: false,
            pii: null,
            isChecking: true
          }
        });
      }
      else {
        tempFiles.push({ ...selectedFiles[index] });
      }
    }
    setSelectedFiles(tempFiles);

    await Promise.all(tempFiles.map(async selectedFile => {
      if (selectedFile.pii?.isChecking) {
        const isAspx = selectedFile.path?.indexOf('.aspx') > -1;
        if (!isAspx) {
          let fileUrl = '';
          let siteUrl = '';

          if (selectedFile.siteUrl) {
            fileUrl = selectedFile.fullWebUrl
            siteUrl = selectedFile.siteUrl
          } else {
            fileUrl = !currentDirectory.directory ? `${currentDirectory.webUrl}/${selectedFile.name}` : `${currentDirectory.webUrl}/${currentDirectory.directory}/${selectedFile.name}`;
            siteUrl = currentDirectory.webUrl
          }

          await checkPiiMutation({
            accessToken: await authModule.getSharePointAccessToken(),
            fileUrl,
            siteUrl,
            forceRecheck: true,
            redactPiiData
          })
            .unwrap()
            .then(res => {
              selectedFile.pii = res;

              setSelectedFiles([...tempFiles]);
              updateMultipleFilesPii([...tempFiles]);
            }).catch(error => {
              selectedFile.pii.piiDetected = false;
              selectedFile.pii.piiError = languageStrings.ErrorConnectingToServer;

              setSelectedFiles([...tempFiles]);
              updateMultipleFilesPii([...tempFiles]);
            });
        }
        else {
          await checkGraphPiiMutation({
            accessToken: await authModule.getGraphAccessToken(),
            driveId: selectedFile.driveId,
            itemId: selectedFile.id,
            fileName: selectedFile.name,
            redactPiiData
          })
            .unwrap()
            .then(res => {
              if (res.piiError) {
                delete selectedFile.pii;
              } else {
                selectedFile.pii = res;
              }

              setSelectedFiles([...tempFiles]);
              updateMultipleFilesPii([...tempFiles]);
            }).catch(error => {
              selectedFile.pii.piiDetected = false;
              selectedFile.pii.piiError = languageStrings.ErrorConnectingToServer;

              setSelectedFiles([...tempFiles]);
              updateMultipleFilesPii([...tempFiles]);
            });
        }
      }
    }));

    setIsCheckingPii(false);
    setIsRedactingData(false);
  }

  const piiCheck = props.user.tenantEnablePII && selectedFiles.some(x => x.pii) && (
    <ActionButton
      iconProps={privacySensitiveIcon}
      allowDisabledFocus
      disabled={isCheckingPii || isRedactingData}
      onClick={() => checkPii(false)}>
      <span>{languageStrings.PrivacySensitiveDisplay}</span>
    </ActionButton>
  );

  const piiRedact = showRedact && (
    <Stack horizontal>
      <ActionButton
        iconProps={redactIcon}
        allowDisabledFocus
        onClick={() => checkPii(true)}
        disabled={isCheckingPii || isRedactingData}
        text={languageStrings.PrivacySensitiveRedact}
      />

    </Stack>
  );

  const copySelectedEmails = currentDirectory.type === ComponentTypes.Exchange && (
    <Stack horizontal>
      <ActionButton
        iconProps={saveIcon}
        allowDisabledFocus
        onClick={() => {
          setContentManagerMoveTo({
            mode: ContentManagerCopyMoveModeEnum.CopySelectedEmails,
            moveToEnabled: true,
            workingDirectories: workingDirectories,
            selectedFiles: [],
            siteUrl: "",
            sourceSiteUrl: "",
            sourceServerRelPath: "",
            busyMovingFiles: false,
            copyJobResults: [],
            isMoveComplete: false,
            isMoveCancelled: false,
            destinationDirectory: {},
            progressCountCompleted: 0,
            progressCountTotal: 0
          });

          openConfirmSave();
        }}
        text={languageStrings.SaveSelectedFiles}
      />
    </Stack>
  );

  const getDriveInfo = (fileInfo): IDriveInfo => {
    return !fileInfo.folderId
      ? { id: fileInfo.driveId, driveId: null }
      : { id: fileInfo.folderId, driveId: fileInfo.driveId };
  };

  const insertLinkOrAttachments = async (insertAs: string) => {
    let items: IFileItem[] = [];
    setLinkCount(0);
    if (insertAs === "links") {
      setLoadingLinks(true);
    }
    if (insertAs === "attachments") {
      setLoadingAttachments(true);
    }

    files.forEach(async (file) => {
      let fileInfo = {
        ...file,
        isOpen: true,
        itemId: file?.hasGraphItemId ? file.id : null,
        folderId: !currentDirectory?.parentId ? null : currentDirectory.id,
        filePath:
          file?.hasGraphItemId && file?.path
            ? `[${languageStrings.Files}] ${workingDirectories
              .slice(0, -1)
              .map((x) => x.displayName)
              .join("/")}${file.path}`
            : `[${languageStrings.Files}] ${workingDirectories.map((x) => x.displayName).join("/")}/${file.name}`,
      };

      if (isGraphQuery) {
        const webUrl = await getItemWebViewUrl(
          await authModule.getGraphAccessToken(),
          fileInfo.driveId,
          fileInfo.itemId
        );

        items.push({ id: fileInfo.itemId, driveId: fileInfo.driveId, name: fileInfo.name, webUrl: webUrl });
        setLinkCount(items.length);
      } else {
        const fileItem = await getFileItemInfo(getDriveInfo(fileInfo), fileInfo.name, "id,webUrl");

        items.push({ id: fileItem.id, driveId: fileInfo.driveId, name: fileInfo.name, webUrl: fileItem.webUrl });
        setLinkCount(items.length);
      }

      if (items.length === files.length) {
        if (insertAs === "links") {
          const emailer = emailCreatorfactory.create(languageStrings, mode);

          let links = [];

          items.forEach((item) => {
            links.push({ id: item?.id, driveId: item.driveId, name: item.name, webUrl: item.webUrl });
          });

          setLoadingLinks(false);
          await emailer.withShareableLink(links);
        }
        if (insertAs === "attachments") {
          setLoadingAttachments(false);
          const emailer = emailCreatorfactory.create(languageStrings, mode);

          let attachments = [];
          items.forEach((item) => {
            attachments.push({ id: item?.id, driveId: item.driveId, name: item.name });
          });

          for (let i = 0; i < attachments.length; i += 20) {
            const partialAttachments = attachments.slice(i, i + 20);
            await emailer.withAttachment(partialAttachments);
          }
        }
      }
    });
  };

  const attachmentButtons = (props.mode === ModeEnum.mailCompose || props.mode === ModeEnum.mailRead) && currentDirectory.type !== ComponentTypes.Exchange && (
    <Stack>
      {mode === ModeEnum.mailCompose &&
        (!loadingAttachments ? (
          <ActionButton
            iconProps={attachIcon}
            allowDisabledFocus
            onClick={async () => {
              await insertLinkOrAttachments("attachments");
              setIsMultipleFilesSelectedPanelOpen(false);
            }}
          >
            {languageStrings.InsertAsAttachment}
          </ActionButton>
        ) : (
          <ActionButton iconProps={linkIcon} allowDisabledFocus disabled>
            {`Loading ${linkCount} of ${files.length}`}
          </ActionButton>
        ))}
      {!isMobile() && !loadingLinks ? (
        <ActionButton
          iconProps={linkIcon}
          allowDisabledFocus
          onClick={async () => {
            await insertLinkOrAttachments("links");
            setIsMultipleFilesSelectedPanelOpen(false);
          }}
        >
          {props.mode === ModeEnum.mailCompose ? languageStrings.InsertAsLink : languageStrings.CreateEmailWithLink}
        </ActionButton>
      ) : (
        <ActionButton iconProps={linkIcon} allowDisabledFocus disabled>
          {`Loading ${linkCount} of ${files.length}`}
        </ActionButton>
      )}
    </Stack>
  );

  const [showMoreIsClicked, setShowMore] = React.useState<boolean>(false);
  const [sectionStyle, setSectionStyle] = React.useState<any>();

  React.useEffect(() => {
    const itemHeight = isMobile() ? mobileListItemStyle.height : 40;
    let containerHeight = itemHeight * files.length;

    if (!showMoreIsClicked) {
      containerHeight = files.length > minItems ? itemHeight * minItems : itemHeight * files.length;
    }

    setSectionStyle(
      mergeStyles({
        maxHeight: containerHeight,
        overflow: "hidden",
        transition: "max-height 0.5s ease-in-out",
      })
    );
  }, [showMoreIsClicked]);

  const showMore = files.length > minItems && (
    <div
      className="xb-show-more-action"
      onClick={() => setShowMore((prevShowMoreIsClicked) => !prevShowMoreIsClicked)}
      style={{ marginTop: "5px" }}
    >
      <FontIcon iconName={!showMoreIsClicked ? "ChevronDown" : "ChevronUp"} />
      <span>{!showMoreIsClicked ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
    </div>
  );

  const [isApprovalsPanelOpen, setIsApprovalsPanelOpen] = React.useState(false)
  const approvalsButton = props.user.tenantEnableApprovalHooks && currentDirectory.type !== ComponentTypes.Exchange && approvalFlowsAvailable && (
    <React.Fragment>
      <Stack horizontal>
        <ActionButton
          iconProps={{ iconName: "DocumentApproval" }}
          allowDisabledFocus
          onClick={() => setIsApprovalsPanelOpen(true)}
        >
          <span>{languageStrings.Automation}</span>
        </ActionButton>
      </Stack>
      {isApprovalsPanelOpen && <WebPanel isOpen={isApprovalsPanelOpen} closePanel={() => setIsApprovalsPanelOpen(false)} headerText={languageStrings.AutomationFlowRequest} styles={{ main: { overflow: "unset" } }}>
        <AutomationPanel files={files} isGraphQuery={isGraphQuery} tenantEnablePII={user.tenantEnablePII} closeClicked={() => setIsApprovalsPanelOpen(false)} />
      </WebPanel>}
    </React.Fragment>
  );

  const [isSigningsPanelOpen, setIsSigningsPanelOpen] = React.useState(false);
  const allExtensionsValid = files?.every(x => {
    const extension = x.fileType.toUpperCase();
    return validSigningRequestExtensions.includes(extension);
  });
  const signingRequestButton = CacheHelper.get(DocubirdCache.signingEnabled) === true && allExtensionsValid && currentDirectory.type !== ComponentTypes.Exchange && signingFlowsAvailable && (
    <React.Fragment>
      <Stack horizontal>
        <ActionButton
          iconProps={{ iconName: "InsertSignatureLine" }}
          allowDisabledFocus
          onClick={() => setIsSigningsPanelOpen(true)}
        >
          <span>{languageStrings.SigningRequest}</span>
        </ActionButton>
      </Stack>
      {isSigningsPanelOpen && <WebPanel isOpen={isSigningsPanelOpen} closePanel={() => setIsSigningsPanelOpen(false)} headerText={languageStrings.SigningRequest} styles={{ main: { overflow: "unset" } }}>
        <SigningPanel
          files={files}
          tenantEnablePII={props.user.tenantEnablePII}
          closeClicked={() => setIsSigningsPanelOpen(false)}
          isContentManager={false}
        />
      </WebPanel>}
    </React.Fragment>
  );

  const onConfirmDelete = async () => {
    setIsDeletingFiles(true);

    const isInOneDrive = props?.currentDirectory?.type == ComponentTypes.OneDrive || props?.currentDirectory?.type == ComponentTypes.OneDriveSubFolder;

    await Promise.all(files.map(async file => {
      let fileInfo = {
        ...file,
        isOpen: true,
        itemId: file?.hasGraphItemId ? file.id : null,
        folderId: !currentDirectory?.parentId ? null : currentDirectory.id,
        filePath:
          file?.hasGraphItemId && file?.path
            ? `[${languageStrings.Files}] ${workingDirectories
              .slice(0, -1)
              .map((x) => x.displayName)
              .join("/")}${file.path}`
            : `[${languageStrings.Files}] ${workingDirectories.map((x) => x.displayName).join("/")}/${file.name}`,
      };

      const fileItem = await getFileItemInfo(getDriveInfo(fileInfo), fileInfo.name, "id,webUrl,sharepointIds");

      try {
        let fetchedFile = await getItemByUniqueId({
          siteUrl: fileItem.sharepointIds.siteUrl,
          sharePointListId: fileItem.sharepointIds.listId,
          uniqueId: fileItem.sharepointIds.listItemUniqueId,
          accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
        }).unwrap();

        await sendSharepointItemToRecyclebin({
          siteUrl: fileItem.sharepointIds.siteUrl,
          sharePointListId: fileItem.sharepointIds.listId,
          accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
          itemId: fetchedFile.ID
        }).unwrap();
      } catch (error) {
        logger(error, "MultipleFilesSeleted.tsx (onConfirmDelete)");
      }
    }));

    setIsDeletingFiles(false);
    setConfirmDelete(false);
    filesDeleted();
  }

  const fileDeleteButton = currentDirectory.type !== ComponentTypes.Exchange
    && (props?.currentDirectory?.type == ComponentTypes.OneDrive
      || props?.currentDirectory?.type == ComponentTypes.OneDriveSubFolder
      || props.currentSharepointFolder?.userPermissions?.canDeleteListItem)
    && (
      <Stack horizontal>
        <ActionButton
          iconProps={fileDeleteIcon}
          disabled={isDeletingFiles}
          styles={confirmDelete && confirmDeleteButtonStyles}
          allowDisabledFocus
          text={confirmDelete ? `${languageStrings.Confirm} ${languageStrings.Delete}?` : languageStrings.Delete}
          onClick={() => {
            if (!confirmDelete) {
              setConfirmDelete(true);
            }
          }}
        />
        {isDeletingFiles && <Spinner size={SpinnerSize.small} styles={{ root: { paddingLeft: 8 } }} />}
        {confirmDelete && !isDeletingFiles && <TooltipHost content={languageStrings.Confirm} >
          <ActionButton
            iconProps={{ iconName: "Accept" }}
            onClick={() => onConfirmDelete()}
          />
        </TooltipHost>}
        {confirmDelete && !isDeletingFiles && <TooltipHost content={languageStrings.Cancel} >
          <ActionButton
            iconProps={{ iconName: "Cancel" }}
            onClick={() => setConfirmDelete(false)}
          />
        </TooltipHost>}
      </Stack>
    );

  const propertiesButton = props?.searchValue === "" && (
    <React.Fragment>
      <Stack horizontal>
        <ActionButton
          iconProps={propertiesIcon}
          allowDisabledFocus
          text={languageStrings.Properties}
          onClick={() => {
            props?.openPropertiesTrigger();
          }}
        >
        </ActionButton>
      </Stack>
    </React.Fragment>
  )

  const downloadFiles = async () => {
    try {
      setIsDownloadingFiles(true);

      // const isInOneDrive = props?.currentDirectory?.type == ComponentTypes.OneDrive || props?.currentDirectory?.type == ComponentTypes.OneDriveSubFolder;
      // const driveId = !isInOneDrive ? (!props?.currentDirectory?.parentId ? props?.currentDirectory?.id : props.currentDirectory.parentId) : props?.oneDriveRootDetails.driveId;

      const downloadRequestItems = await Promise.all(files.map(async file => {
        let fileInfo = {
          ...file,
          isOpen: true,
          itemId: file?.hasGraphItemId ? file.id : null,
          folderId: !currentDirectory?.parentId ? null : currentDirectory.id,
          filePath:
            file?.hasGraphItemId && file?.path
              ? `[${languageStrings.Files}] ${workingDirectories
                .slice(0, -1)
                .map((x) => x.displayName)
                .join("/")}${file.path}`
              : `[${languageStrings.Files}] ${workingDirectories.map((x) => x.displayName).join("/")}/${file.name}`,
        };

        const downloadPayload = {
          driveId: file.driveId,
          name: file.name,
          itemId: ""
        }

        const isFileNameDownloadTemplatesAvailable: boolean = getFileNameDownloadTemplateAvailable(props.siteInfo);

        let fileItem: any;

        if (isGraphQuery) {
          if (isFileNameDownloadTemplatesAvailable) {
            fileItem = await getItem(await authModule.getGraphAccessToken(), file.driveId, file.id);
          }
          downloadPayload.itemId = file.id;
        } else {
          fileItem = await getFileItemInfo(getDriveInfo(fileInfo), fileInfo.name, "id,name,webUrl,sharepointIds");
          downloadPayload.itemId = fileItem.id;
        }

        const isOneDriveFile: boolean = fileItem?.webUrl?.startsWith(user?.oneDriveWebUrl ?? "<<undefined>>");
        if (!isOneDriveFile && fileItem?.sharepointIds) {
          const fileNameFromTemplate: string = await getFilenameFromTemplate(
            props.siteInfo,
            file.driveId,
            downloadPayload.itemId,
            fileItem.sharepointIds.siteUrl,
            fileItem.sharepointIds.listId,
            fileItem.sharepointIds.listItemUniqueId);
          if (fileNameFromTemplate) {
            downloadPayload.name = fileNameFromTemplate;
          }
        }

        return downloadPayload;
      }));

      try {
        const downloadRequestPayload = {
          accessToken: await authModule.getGraphAccessToken(),
          items: [...downloadRequestItems]
        };

        await FileDownLoader.downloadFileStream(downloadRequestPayload);
      } catch (error) {
        logger(error, "MultipleFilesSeleted.tsx (downloadFiles downloadFileStream)");
      }

      setIsDownloadingFiles(false);
    } catch (error) {
      logger(error, "MultipleFilesSeleted.tsx (downloadFiles)");
    }
  }

  const fileDownloadButton = currentDirectory.type !== ComponentTypes.Exchange && (
    <React.Fragment>
      <Stack horizontal>
        <ActionButton
          iconProps={fileDownloadIcon}
          disabled={isDownloadingFiles}
          allowDisabledFocus
          text={languageStrings.Download}
          onClick={() => downloadFiles()}
        >
          {isDownloadingFiles && <Spinner size={SpinnerSize.small} />}
        </ActionButton>
      </Stack>
    </React.Fragment>
  );

  const [isSharePanelOpen, setIsSharePanelOpen] = React.useState(false);

  const copyLinkButton = props.enableSharing && (
    <>
      <Stack horizontal>
        <ActionButton
          iconProps={copyLinkIcon}
          allowDisabledFocus
          onClick={async () => {
            setIsSharePanelOpen(true);
          }}
        >
          {languageStrings.CopyLink}
        </ActionButton>
      </Stack>
      {isSharePanelOpen && <CMFileSharePanel panelIsOpen={isSharePanelOpen} closePanel={() => setIsSharePanelOpen(false)} items={selectedFiles} mode={SharePanelMode.Copy} />}
    </>
  )

  return (
    <>
      <div style={{ marginLeft: -15, marginRight: -15 }}>
        <div className={sectionStyle} style={{ paddingTop: "15px" }}>
          {fileViews}
        </div>
      </div>
      {showMore}
      <div style={{ marginTop: "15px" }}>
        {piiCheck}
        {piiRedact}
        {attachmentButtons}
        {copyLinkButton}
        {approvalsButton}
        {signingRequestButton}
        {fileDownloadButton}
        {fileDeleteButton}
        {copySelectedEmails}
        {propertiesButton}
      </div>
    </>
  );
};

export default connector(MultipleFilesSeleted);
